/**
 * RTM services metrics envelope (per spec §20, §30.15).
 */
import { z } from 'zod';

export const RtmServiceMetric = z.object({
  service_name: z.string(),
  // Kubernetes namespace the pod/service lives in. Optional so MQTT-sourced
  // envelopes stay valid; the Grafana/Prometheus source (ClickUp 86e1w8ude)
  // sets it so the Service Log panel can scope its Loki query.
  namespace: z.string().optional(),
  pod_status: z.enum(['running', 'degraded', 'down']),
  uptime_pct: z.number().min(0).max(100),
  rx: z.number().nullable(),
  tx: z.number().nullable(),
  restarts: z.number(),
  msg_in: z.number().nullable(),
  msg_out: z.number().nullable(),
  active: z.number().nullable(),
});

// Per-pod resource snapshot for the Hosting Environment panel
// (mockup §"Running Pods" L3738-L3761). Carried alongside services so the
// whole RTM-context view stays on a single envelope.
export const RtmPodMetric = z.object({
  name: z.string(),
  memPct: z.number().min(0).max(100),
  cpuPct: z.number().min(0).max(100),
  // Absolute working-set memory in MiB (for the Running Pods table's "Mem"
  // column). Optional so MQTT-sourced envelopes stay valid.
  memMiB: z.number().optional(),
});

// Cluster-wide hosting roll-up (mockup §"Hosting Environment" L3719-L3736).
export const RtmHosting = z.object({
  pods_up: z.number(),
  pods_down: z.number(),
  memory_gib: z.number(),
  cpu_pct: z.number().min(0).max(100),
  health: z.enum(['Healthy', 'Degraded', 'Unhealthy']),
  pods: z.array(RtmPodMetric),
});

// Cloud-processing roll-up (mockup §"Apache Flink — FLYsafe Pipeline" inside
// the Agents tab). Lives on the services envelope because the pipeline jobs
// share the broker / pod lifecycle as the RTM services already covered here.
export const CloudJob = z.object({
  name: z.string(),
  status: z.enum(['Online', 'Degraded', 'Offline']),
});
export const CloudProcessing = z.object({
  pipeline_name: z.string(), // e.g. "Apache Flink — FLYsafe Pipeline"
  pipeline_sub: z.string(), // e.g. "RTTP · Strimzi · Kafka Connect"
  status: z.enum(['Running', 'Degraded', 'Stopped']),
  jobs: z.array(CloudJob),
});

export const ServicesEnvelope = z.object({
  services: z.array(RtmServiceMetric),
  // Optional so backwards-compatible with sources that don't supply it
  // (in particular: the live source until a hosting topic ships).
  hosting: RtmHosting.nullable().optional(),
  cloud_processing: CloudProcessing.nullable().optional(),
  received_at: z.string().datetime(),
});

export type RtmServiceMetric = z.infer<typeof RtmServiceMetric>;
export type RtmPodMetric = z.infer<typeof RtmPodMetric>;
export type RtmHosting = z.infer<typeof RtmHosting>;
export type CloudJob = z.infer<typeof CloudJob>;
export type CloudProcessing = z.infer<typeof CloudProcessing>;
export type ServicesEnvelope = z.infer<typeof ServicesEnvelope>;
